import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store, select } from "@ngrx/store";
import { catchError, map, mergeMap, switchMap, tap } from "rxjs/operators";
import { CommandActions, ConditionActions, RuleActions } from "../actions";

import { Injectable } from "@angular/core";
import { LOG_CATEGORY, LOG_SUB_TYPE, LOG_TYPE, createAuditLogKey } from "@app/audit-log/services/audit-log.constants";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { ProjectSelectors } from "@app/project/store";
import { emptyAction } from "@app/root-store/actions/snackbar.action";
import { EffectsUtils } from "@app/root-store/effects/effect-creators";
import { CHRule } from "@app/rule/model/rule";
import { CHRulesService } from "@app/rule/services/ch-rules.service";
import { DeleteConfirmationService } from "@app/shared/components/delete-confirmation/delete-confirmation.service";
import { ActionWord } from "@app/shared/resources/action-word";
import { checkCurrentID } from "@app/shared/resources/data-actions";
import { ClareHomeDbCollection } from "@app/shared/resources/resource-objectifier";
import { of } from "rxjs";
import { CHRulesDbService } from "../../services/ch-rules-db.service";
import { BulkActionPayload } from "../actions/ch-rules.actions";

@Injectable()
export class RuleEffects extends EffectsUtils {
    constructor(
        actions$: Actions,
        private readonly ruleService: CHRulesDbService,
        private readonly deleteConfirmationService: DeleteConfirmationService,
        private readonly store: Store,
        private readonly auditLogService: AuditLogService,
        private readonly chRulesService: CHRulesService
    ) {
        super(actions$);
    }

    getRulesFailure$ = this.createFailureEffect(
        RuleActions.loadSet.failure,
        null,
        null,
        $localize`:@@ERROR.SERVICE.GETTING_ACTION:Error getting actions`
    );

    getRules$ = this.createSwitchEffect({
        dbMethod: ({ uuid }) =>
            this.ruleService.getRules(uuid).pipe(
                checkCurrentID<ClareHomeDbCollection<CHRule>>(this.store.pipe(select(ProjectSelectors.getUuid)), uuid),
                switchMap(rules => this.ruleService.checkRuleTypes(rules)),
                map(rules => ({ uuid, rules }))
            ),
        actionSet: RuleActions.loadSet,
    });

    createRule$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RuleActions.createSet.request),
            switchMap(({ uuid, rule, callbackEmitter }) =>
                this.ruleService.createRule(uuid, rule).pipe(
                    checkCurrentID(this.store.pipe(select(ProjectSelectors.getUuid)), uuid),
                    mergeMap(_ => {
                        const successAction = RuleActions.createSet.success({ uuid, rule });
                        callbackEmitter.emit(successAction);
                        return [
                            RuleActions.clearRuleState(),
                            CommandActions.clearCommandState(),
                            ConditionActions.clearConditionState(),
                            successAction,
                        ];
                    }),
                    catchError(error => {
                        callbackEmitter.emit(error);
                        return of(RuleActions.createSet.failure({ error }));
                    })
                )
            )
        )
    );

    typeName = $localize`:@@ERROR.SERVICE.ACTION:action`;

    createRuleSuccess$ = this.createSuccessEffect(RuleActions.createSet.success, ActionWord.saved, this.typeName);
    createRuleFailure$ = this.createFailureEffect(RuleActions.createSet.failure, ActionWord.saved, this.typeName);

    deleteRule$ = this.createExhaustEffect({
        dbMethod: ({ uuid, rule, project }) =>
            this.ruleService.deleteRule(uuid, rule).pipe(
                tap(_ => {
                    const logKey = createAuditLogKey(LOG_CATEGORY.ACCOUNT, LOG_TYPE.ACTION, LOG_SUB_TYPE.DELETE);
                    this.auditLogService.logEvent(logKey, {
                        optionalParams: { uuid },
                        projectId: project?.id,
                        entityId: project?.entityId,
                        data: `Name: ${rule.name}, Type: ${this.chRulesService.getCHRuleFromCHRuleInt(rule).ruleType}`,
                    });
                })
            ),
        actionSet: RuleActions.deleteSet,
    });

    deleteRuleConfirmation$ = this.createDeleteConfirmationEffect({
        actionSet: RuleActions.deleteConfirmationSet,
        dbMethod: ({ rule, uuid, project }) => of({ payload: { rule, uuid, project }, display: rule.name }),
        confirmationService: this.deleteConfirmationService,
    });

    deleteRuleSuccess$ = this.createSuccessEffect(RuleActions.deleteSet.success, ActionWord.deleted, this.typeName);
    deleteRuleFailure$ = this.createFailureEffect(RuleActions.deleteSet.failure, ActionWord.deleted, this.typeName);

    // Delete All Effects
    deleteAllRules$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RuleActions.deleteAll),
            switchMap(({ uuid, rules, callbackEmitter }) =>
                this.ruleService.deleteAllRules(uuid, rules).pipe(
                    switchMap(_ => {
                        const successAction = RuleActions.deleteAllSuccess({ uuid, rules });
                        callbackEmitter.emit(successAction);
                        return [
                            RuleActions.clearRuleState(),
                            CommandActions.clearCommandState(),
                            ConditionActions.clearConditionState(),
                            successAction,
                        ];
                    }),
                    catchError(error => {
                        callbackEmitter.emit(error);
                        return of(RuleActions.deleteAllFailure({ uuid, error }));
                    })
                )
            )
        )
    );

    deleteAllConfirmation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RuleActions.deleteAllConfirmation),
            switchMap(({ rules, uuid, callbackEmitter }) =>
                this.deleteConfirmationService.open$<BulkActionPayload>(
                    { rules, uuid, callbackEmitter },
                    RuleActions.deleteAllDismiss,
                    RuleActions.deleteAll,
                    `all ${this.typeName}s from this project`
                )
            )
        )
    );
    deleteAllRulesSuccess$ = this.createSuccessEffect(RuleActions.deleteAllSuccess, ActionWord.deleted, `${this.typeName}s`);
    deleteAllRulesFailure$ = this.createFailureEffect(RuleActions.deleteAllFailure, ActionWord.deleted, `${this.typeName}s`);
    deleteAllConfirmationDismiss$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RuleActions.deleteAllDismiss),
            switchMap(({ callbackEmitter }) => {
                callbackEmitter.emit();
                return [emptyAction()];
            })
        )
    );
}
